import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import { useOutsideClick } from '../../utils';
import { theme } from '../../styles';
import { HelpIllustration } from '../../assets/illustrations';
import { Headings } from '../Headings';
import { Box } from '../Layout';
import { CancelIcon } from '../../assets/icons';

type FAQModalContainerProps = {
  children: React.ReactNode;
};

const DrawerToggleButton = styled.button`
  display: flex;
  position: fixed;
  bottom: 51%;
  right: 1.25rem;
  padding: 0;
  border: none;
  background: transparent;
  appearance: none;
  user-select: none;
  transition: transform 0.1s ease-in-out;
  z-index: ${theme.zIndex.modal};
  &:hover {
    cursor: pointer;
    transform: scale(1.1);
  }
`;

const DrawerContainer = styled(motion.div)<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  right: 0;
  ${({ $isOpen }) => $isOpen && `z-index: ${theme.zIndex.modal};`}
  width: 100%;
  height: 100%;
  background: ${theme.colors.black10};
`;

const SafariOverflowFix = styled(motion.div)`
  overflow: auto;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;

const DrawerHeader = styled(Box)`
  position: sticky; /* NOT SUPPORTED BY IE11 */
  top: 0;
  left: 0;
  right: 0;
  z-index: ${theme.zIndex.modal + 1};
  padding: 0;
  padding-bottom: 60px;
  padding-top: 40px;
  background: ${theme.colors.black10};
  border-top: solid 1px ${theme.colors.black30};
`;

const DrawerBody = styled(Box)``;

const DrawerIconWrapper = styled(motion.div)`
  z-index: 5100;
  position: fixed;
  top: 110%;
  left: 0;
  right: 0;
  text-align: center;
  transform: translate(0, -50%);
`;

const animation = {
  initial: { scale: 0 },
  animate: { scale: 1 },
  exit: { scale: 0 },
  transition: { duration: 0.1 },
};

export const ToggleIcon = ({ isOpen }: { isOpen: boolean }) => {
  return (
    <AnimatePresence mode="wait" initial={false}>
      {isOpen ? (
        <motion.div key="0" {...animation}>
          <CancelIcon color={'black90'} width={50} />
        </motion.div>
      ) : (
        <motion.div key="1" {...animation}>
          <HelpIllustration width={50} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const FAQDrawerContainer = ({ children }: FAQModalContainerProps) => {
  const modalContainer = useRef<HTMLDivElement>(null);
  const drawerHeight = 50;

  const [isOpen, setIsOpen] = useState(false);
  const onToggle = () => {
    if (!isOpen) {
      modalContainer.current?.focus();
    }
    setIsOpen(!isOpen);
  };

  useOutsideClick(modalContainer, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    if (modalContainer.current) {
      if (isOpen) {
        disableBodyScroll(modalContainer.current);
      } else {
        enableBodyScroll(modalContainer.current);
      }
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  });

  return (
    <>
      <DrawerIconWrapper animate={{ top: isOpen ? `${100 - drawerHeight}%` : '110%' }} transition={{ duration: 0.2 }}>
        <HelpIllustration title="Vanliga frågor" />
      </DrawerIconWrapper>
      <DrawerContainer
        ref={modalContainer}
        initial={{ height: 0 }}
        animate={{ height: isOpen ? `${drawerHeight}%` : 0 }}
        transition={{ duration: 0.2 }}
        tabIndex={isOpen ? 0 : -1}
        $isOpen={isOpen}
      >
        <SafariOverflowFix>
          {isOpen && (
            <>
              <DrawerHeader paddingTop={20} paddingRight={20} paddingBottom={40} paddingLeft={20} textAlign="center">
                <Headings.H3>Vanliga frågor</Headings.H3>
              </DrawerHeader>
              <DrawerBody>
                <Box maxWidth={['100%', 560, 578]} marginLeft="auto" marginRight="auto">
                  {children}
                </Box>
              </DrawerBody>
            </>
          )}
          <DrawerToggleButton onClick={onToggle}>
            <ToggleIcon isOpen={isOpen} />
          </DrawerToggleButton>
        </SafariOverflowFix>
      </DrawerContainer>
    </>
  );
};
